// services/sponsorshipService.js
// Public sponsorship API. None of these calls are Cognito-authenticated: the submission
// form is open, and the sponsor portal is secured by a per-sponsorship magic-link token.

const API_BASE_URL = 'https://xoxl3mvo9e.execute-api.us-east-1.amazonaws.com/prod';

async function parseError(response, fallback) {
  try {
    const data = await response.json();
    return data.message || JSON.stringify(data);
  } catch (e) {
    try { return await response.text(); } catch (_) { return fallback; }
  }
}

/**
 * Submit a new sponsorship request.
 * @param {Object} data - { companyName, contactName, contactEmail, destinationUrl, logoData }
 *                         logoData is a base64 data URL of the logo image.
 */
export const submitSponsorship = async (data) => {
  const response = await fetch(`${API_BASE_URL}/sponsorships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await parseError(response, `Failed to submit (${response.status})`));
  return response.json();
};

/** Load the sponsor portal view (status, metrics, renewal, pending edit) by access token. */
export const getSponsorship = async (token) => {
  const response = await fetch(`${API_BASE_URL}/sponsorships/portal?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(await parseError(response, `Failed to load sponsorship (${response.status})`));
  return response.json();
};

/** Submit an edit to an existing sponsorship (triggers admin re-approval). */
export const updateSponsorship = async (token, data) => {
  const response = await fetch(`${API_BASE_URL}/sponsorships/portal/edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...data }),
  });
  if (!response.ok) throw new Error(await parseError(response, `Failed to submit changes (${response.status})`));
  return response.json();
};

/** Start the $100/30-day Stripe Checkout subscription. Returns { url } to redirect to. */
export const startCheckout = async (token) => {
  const response = await fetch(`${API_BASE_URL}/sponsorships/portal/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error(await parseError(response, `Failed to start checkout (${response.status})`));
  return response.json();
};

/** Open the Stripe billing portal (update card / cancel). Returns { url }. */
export const openBillingPortal = async (token) => {
  const response = await fetch(`${API_BASE_URL}/sponsorships/portal/billing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error(await parseError(response, `Failed to open billing portal (${response.status})`));
  return response.json();
};

const sponsorshipService = { submitSponsorship, getSponsorship, updateSponsorship, startCheckout, openBillingPortal };
export default sponsorshipService;
